'use client';

import './chip-group.scss';

import React, { useEffect, useState } from 'react';

import { trackClick } from '@sola/cookies/components/datalayer.js';

import { Chip } from '../../atoms/chip/chip';
import { cn } from '../../utils/helpers';

export const handleChipClick = (chip: Chip, selected: Chip[]): Chip[] => {
  if (chip.isClear) {
    return [chip];
  }

  const updated = [...selected];
  const index = selected.findIndex((c) => c.value === chip.value);

  if (index !== -1) {
    updated.splice(index, 1);
  } else {
    updated.push(chip);
  }

  return updated.filter((c) => !c.isClear);
};

export interface ChipGroupProps {
  chips: Chip[];
  handleSelectionChange: (selected: string[]) => void;
  className?: string;
  classNameChip?: string;
  removable?: boolean;
  selectedValues?: string[];
  onRemoveChip?: (value: string) => void;
  chipSize?: 'sm' | 'md';
  isScrollable?: boolean;
}

export const ChipGroup: React.FC<ChipGroupProps> = ({
  chips,
  handleSelectionChange,
  className = 'gap-comfy-sm',
  classNameChip,
  removable = false,
  selectedValues,
  onRemoveChip,
  chipSize = 'md',
  isScrollable = false,
}) => {
  const [internalSelected, setInternalSelected] = useState<Chip[]>(
    chips.filter((c) => c.isSelected),
  );

  const selectedChips = selectedValues
    ? chips.filter((chip) => selectedValues.includes(chip.value))
    : internalSelected;

  useEffect(() => {
    if (!selectedValues) {
      handleSelectionChange(internalSelected.map((c) => c.value));
    }
  }, [internalSelected, selectedValues, handleSelectionChange]);

  const handleClick = (chip: Chip) => {
    trackClick(chip.name, chip.value, className || 'ChipGroup');
    const updated = handleChipClick(chip, selectedChips);

    if (selectedValues) {
      handleSelectionChange(updated.map((c) => c.value));
    } else {
      setInternalSelected(updated);
    }
  };

  return (
    <ul
      className={cn(
        'ChipGroup flex m-none p-none',
        { 'ChipGroup--scrollable': isScrollable },
        className,
      )}
    >
      {chips.map((chip) => {
        const isSelected =
          typeof chip.isSelected === 'boolean'
            ? chip.isSelected
            : selectedChips.some((c) => c.value === chip.value);

        return (
          <li key={chip.value}>
            <Chip
              {...chip}
              isSelected={isSelected}
              onClick={() => handleClick(chip)}
              onRemove={
                removable
                  ? () =>
                      onRemoveChip
                        ? onRemoveChip(chip.value)
                        : setInternalSelected((prev) =>
                            prev.filter((c) => c.value !== chip.value),
                          )
                  : undefined
              }
              removable={removable}
              className={classNameChip}
              size={chipSize}
            />
          </li>
        );
      })}
    </ul>
  );
};
